import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sun, Moon, Mail, Lock, User, Building2 } from 'lucide-react';
import { signInWithRedirect, signIn, signUp, confirmSignUp } from 'aws-amplify/auth';
import FloatingLines from './reactbits/FloatingLines';
import FadeIn from './reactbits/FadeIn';
import GradientText from './reactbits/GradientText';
import CountUp from './reactbits/CountUp';
import TrueFocus from './reactbits/TrueFocus';
import RotatingText from './reactbits/RotatingText';

const TABS = [
  { id: 'login', label: 'Login' },
  { id: 'signup', label: 'Sign Up' },
];

const ROTATING_EXAMPLES = [
  'email triage workflow',
  'lead scoring pipeline',
  'compliance reviewer',
  'content moderator',
  'onboarding assistant',
];

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const returnTo = from ? `${from.pathname}${from.search || ''}` : '/';
  const [activeTab, setActiveTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [authError, setAuthError] = useState('');
  const [bannerHovered, setBannerHovered] = useState(false);
  const brandRef = useRef(null);
  const lensRef = useRef(null);

  // Helper for minimum delay
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleBrandMouseMove = useCallback((e) => {
    const rect = brandRef.current?.getBoundingClientRect();
    if (!rect || !lensRef.current) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    lensRef.current.style.setProperty('--lx', `${x}px`);
    lensRef.current.style.setProperty('--ly', `${y}px`);
    lensRef.current.style.opacity = '1';
    lensRef.current.style.clipPath = `circle(70px at ${x}px ${y}px)`;
  }, []);

  const handleBrandMouseLeave = useCallback(() => {
    if (!lensRef.current) return;
    lensRef.current.style.opacity = '0';
    lensRef.current.style.clipPath = `circle(0px at var(--lx) var(--ly))`;
  }, []);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });

  const toggleTheme = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (companyName.trim()) {
      localStorage.setItem('companyName', companyName.trim());
    }
    setAuthError('');
    setIsAuthLoading(true);
    setLoadingText(activeTab === 'login' ? 'Securing Session...' : 'Creating Account...');

    try {
      if (activeTab === 'login') {
        const authPromise = signIn({
          username: email,
          password: password,
        });

        // Minimum 1s delay for smooth premium feel
        const [authResult] = await Promise.all([authPromise, sleep(1200)]);
        const { isSignUpComplete, nextStep } = authResult;

        if (nextStep.signInStep === 'DONE') {
          navigate(returnTo);
        }
      } else {
        const nameParts = name.trim().split(/\s+/);
        const givenName = nameParts[0] || '';
        const familyName = nameParts.slice(1).join(' ') || '.';

        const authPromise = signUp({
          username: email,
          password: password,
          options: {
            userAttributes: {
              email: email,
              given_name: givenName,
              family_name: familyName,
            },
          },
        });

        const [authResult] = await Promise.all([authPromise, sleep(1200)]);
        const { isSignUpComplete, userId, nextStep } = authResult;

        if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
          setIsVerifying(true);
        } else {
          navigate(returnTo);
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      const msg = error.message || '';
      const code = error.name || error.code || '';
      // Cognito: duplicate email on signup
      if (code === 'UsernameExistsException' || msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('already a user')) {
        setAuthError('An account with this email already exists. Please sign in instead.');
        setActiveTab('login');
      } else if (code === 'UserNotConfirmedException') {
        // Account exists but never confirmed — jump to verification
        setIsVerifying(true);
        setAuthError('Your account was created but not verified. Please enter the code sent to your email.');
      } else {
        setAuthError(msg || 'Authentication failed. Please try again.');
      }
    } finally {
      setIsAuthLoading(false);
      setLoadingText('');
    }
  };

  const handleConfirmCode = async (e) => {
    e.preventDefault();
    setIsAuthLoading(true);
    setLoadingText('Verifying Code...');

    try {
      const authPromise = confirmSignUp({
        username: email,
        confirmationCode: confirmationCode,
      });

      const [authResult] = await Promise.all([authPromise, sleep(1000)]);
      const { isSignUpComplete, nextStep } = authResult;

      if (nextStep.signUpStep === 'DONE' || isSignUpComplete) {
        setIsVerifying(false);
        setActiveTab('login');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setAuthError(error.message || 'Verification failed. Please try again.');
    } finally {
      setIsAuthLoading(false);
      setLoadingText('');
    }
  };

  const handleSocialLogin = async (provider) => {
    if (companyName.trim()) {
      localStorage.setItem('companyName', companyName.trim());
    }
    // Save intended destination so we can redirect after Cognito OAuth callback
    if (returnTo && returnTo !== '/') {
      localStorage.setItem('bytesized_login_redirect', returnTo);
    }
    // Map internal provider names to Cognito expected values
    const providerMap = {
      'google': 'Google',
      'github': 'github', // Matches reference repo configuration
    };

    const cognitoProvider = providerMap[provider];

    try {
      console.log(`Initiating social login for provider: ${provider} (Cognito: ${cognitoProvider})`);
      await signInWithRedirect({
        provider: {
          custom: cognitoProvider || provider,
        },
      });
    } catch (error) {
      console.error('Sign in redirect failed', error);
      alert('Authentication redirect failed: ' + (error.message || 'Unknown error'));
      // Removed automatic navigate(returnTo) fallback to allow user to see the error
    }
  };

  const dark = isDarkMode;

  return (
    <div className={`relative min-h-screen overflow-hidden ${dark ? 'bg-[#030d1a]' : 'bg-[#e8f4f8]'}`}>
      <style>{`
        @keyframes brandPulse {
          0%, 100% { opacity: 0.85; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* ── Full-screen FloatingLines background ───────────────── */}
      <div className="absolute inset-0 z-0" style={{ opacity: dark ? 1 : 0.3 }}>
        <FloatingLines
          linesGradient={dark
            ? ['#0e7490', '#0891b2', '#06b6d4', '#22d3ee', '#67e8f9', '#0ea5e9']
            : ['#0077b6', '#0096c7', '#00b4d8', '#48cae4', '#0077b6', '#00b4d8']
          }
          enabledWaves={['top', 'middle', 'bottom']}
          lineCount={[5, 4, 3]}
          lineDistance={[4, 5, 3]}
          animationSpeed={0.5}
          interactive={true}
          bendRadius={5.0}
          bendStrength={-0.5}
          mouseDamping={0.04}
          parallax={true}
          parallaxStrength={0.15}
        />
      </div>

      {/* ── Main layout — pointer-events pass through to FloatingLines ── */}
      <div className="relative z-10 min-h-screen flex items-stretch" style={{ pointerEvents: 'none' }}>

        {/* ── Left: Hero content ─────────────────────────────── */}
        <div className="hidden lg:flex lg:flex-col lg:w-[45%] px-16 xl:px-20 pt-20">
          <div style={{
            textShadow: dark
              ? '0 2px 20px rgba(3,13,26,0.8), 0 0 40px rgba(3,13,26,0.6)'
              : 'none'
          }}>
            {/* Branding — top, with fluid glass lens on hover */}
            <FadeIn delay={0.1} duration={0.6}>
              <div className="mb-16">
                <div
                  ref={brandRef}
                  className="relative"
                  style={{ padding: '12px 0', cursor: 'none', pointerEvents: 'auto' }}
                  onMouseMove={handleBrandMouseMove}
                  onMouseLeave={handleBrandMouseLeave}
                >
                  {/* Base text layer */}
                  <h1 className="text-6xl font-bold">
                    <GradientText
                      colors={dark
                        ? ['#22d3ee', '#67e8f9', '#a5f3fc', '#22d3ee']
                        : ['#064e5e', '#0e7490', '#0891b2', '#064e5e']
                      }
                      animationSpeed={6}
                    >
                      BytesizedAI
                    </GradientText>
                  </h1>

                  {/* Glass lens overlay — magnified duplicate clipped to circle */}
                  <div
                    ref={lensRef}
                    className="absolute pointer-events-none"
                    style={{
                      top: 0, left: 0, right: 0, bottom: 0,
                      opacity: 0,
                      clipPath: 'circle(0px at 0px 0px)',
                      transition: 'opacity 0.3s ease',
                      '--lx': '0px',
                      '--ly': '0px',
                    }}
                  >
                    {/* Magnified content — scaled from cursor origin */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        transform: 'scale(1.5)',
                        transformOrigin: 'var(--lx) var(--ly)',
                        filter: dark ? 'brightness(1.5) drop-shadow(0 0 15px rgba(34,211,238,0.6))' : 'brightness(1.2) drop-shadow(0 0 10px rgba(8,145,178,0.4))',
                      }}
                    >
                      <h1 className="text-6xl font-bold">
                        <GradientText
                          colors={dark
                            ? ['#a5f3fc', '#cffafe', '#ffffff', '#a5f3fc']
                            : ['#0891b2', '#06b6d4', '#22d3ee', '#0891b2']
                          }
                          animationSpeed={3}
                        >
                          BytesizedAI
                        </GradientText>
                      </h1>
                      <p className={`text-base font-light tracking-widest uppercase mt-2 ${dark ? 'text-cyan-100' : 'text-cyan-700'}`}>
                        Know what AI can do before your sprint begins
                      </p>
                    </div>

                    {/* Glass sphere */}
                    <div
                      className="absolute rounded-full pointer-events-none"
                      style={{
                        width: '140px',
                        height: '140px',
                        transform: 'translate(-50%, -50%)',
                        left: 'var(--lx)',
                        top: 'var(--ly)',
                        border: dark ? '2px solid rgba(34, 211, 238, 0.35)' : '2px solid rgba(8, 145, 178, 0.3)',
                        boxShadow: dark
                          ? '0 12px 48px rgba(0,0,0,0.5), 0 0 30px rgba(34, 211, 238, 0.25), inset 0 -30px 50px rgba(0,0,0,0.4), inset 0 15px 30px rgba(34,211,238,0.12)'
                          : '0 12px 48px rgba(0,0,0,0.2), 0 0 25px rgba(8, 145, 178, 0.15), inset 0 -30px 50px rgba(0,0,0,0.15), inset 0 15px 30px rgba(255,255,255,0.4)',
                        background: dark
                          ? `radial-gradient(circle at 35% 30%, rgba(255,255,255,0.18) 0%, transparent 40%),
                             radial-gradient(circle at 50% 50%, transparent 40%, rgba(0,0,0,0.35) 100%),
                             radial-gradient(circle at 65% 70%, rgba(34,211,238,0.1) 0%, transparent 50%)`
                          : `radial-gradient(circle at 35% 30%, rgba(255,255,255,0.5) 0%, transparent 40%),
                             radial-gradient(circle at 50% 50%, transparent 40%, rgba(0,0,0,0.15) 100%),
                             radial-gradient(circle at 65% 70%, rgba(8,145,178,0.1) 0%, transparent 50%)`,
                      }}
                    >
                      {/* Specular highlight */}
                      <div
                        className="absolute rounded-full"
                        style={{
                          width: '40px',
                          height: '28px',
                          top: '15px',
                          left: '25px',
                          background: dark
                            ? 'radial-gradient(ellipse, rgba(255,255,255,0.45) 0%, transparent 70%)'
                            : 'radial-gradient(ellipse, rgba(255,255,255,0.65) 0%, transparent 70%)',
                          filter: 'blur(5px)',
                          transform: 'rotate(-20deg)',
                        }}
                      />
                      {/* Secondary highlight */}
                      <div
                        className="absolute rounded-full"
                        style={{
                          width: '16px',
                          height: '12px',
                          top: '25px',
                          left: '52px',
                          background: 'radial-gradient(ellipse, rgba(255,255,255,0.55) 0%, transparent 70%)',
                          filter: 'blur(2px)',
                          transform: 'rotate(-15deg)',
                        }}
                      />
                    </div>
                  </div>
                  {/* Base tagline */}
                  <p className={`text-base font-light tracking-widest uppercase mt-2 ${dark ? 'text-cyan-200/50' : 'text-cyan-800/70'}`}>
                    Know what AI can do before your sprint begins
                  </p>

                  {/* Lens overlay now covers tagline too */}
                </div>
              </div>
            </FadeIn>

            {/* Stat — pushed down below wave */}
            <FadeIn delay={0.3} duration={0.6}>
              <div className="mt-72">
                <p className={`text-2xl leading-relaxed font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Only{' '}
                  <span className={`text-5xl font-black tabular-nums tracking-tight ${dark ? 'text-cyan-300' : 'text-cyan-600'}`}>
                    <CountUp from={4} to={1} duration={2.5} />
                  </span>
                  {' '}in{' '}
                  <span className={`text-3xl font-black tabular-nums tracking-tight ${dark ? 'text-white' : 'text-gray-900'}`}>
                    4
                  </span>{' '}
                  AI initiatives successfully scale beyond pilot stage.
                </p>
              </div>
            </FadeIn>

            {/* Headline */}
            <FadeIn delay={0.5} duration={0.6}>
              <div className="mb-10">
                <TrueFocus
                  sentence="Prototype first.|Commit later."
                  separator="|"
                  manualMode={false}
                  blurAmount={4}
                  borderColor={dark ? '#22d3ee' : '#0891b2'}
                  glowColor={dark ? 'rgba(34, 211, 238, 0.6)' : 'rgba(8, 145, 178, 0.4)'}
                  animationDuration={0.5}
                  pauseBetweenAnimations={1.8}
                  className={`text-5xl font-bold leading-tight ${dark ? 'text-white' : 'text-gray-900'}`}
                />
              </div>
            </FadeIn>
          </div>
        </div>

        {/* ── Right: Login console + banner ─────────────────── */}
        <div className="w-full lg:w-[55%] flex">
          <FadeIn delay={0.15} duration={0.6}>
            <div className="relative flex min-h-screen lg:min-h-[calc(100vh-4rem)] lg:my-8">

              {/* ── Banner layer (behind form) — left edge slides on hover ── */}
              <div
                className="hidden lg:block absolute z-0"
                style={{
                  left: '-80px',
                  right: '-60px',
                  top: '15%',
                  bottom: '15%',
                  pointerEvents: 'auto',
                }}
                onMouseEnter={() => setBannerHovered(true)}
                onMouseLeave={() => setBannerHovered(false)}
              >
                <div
                  className={`absolute inset-0 rounded-2xl backdrop-blur-sm transition-all duration-700 ease-in-out ${dark
                    ? 'bg-gray-950/40 border border-cyan-500/5'
                    : 'bg-white/25 border border-gray-300/20'
                    }`}
                  style={{
                    left: bannerHovered ? '80px' : '0px',
                  }}
                />
              </div>

              {/* ── Login console (above banner) ────────────────────── */}

              {/* Vertical side tabs — aligned to top, darker */}
              <div className="hidden sm:flex flex-col pt-10 -mr-px z-20" style={{ pointerEvents: 'auto' }}>
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setAuthError(''); }}
                    className={`relative px-4 py-5 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${tab.id === 'login' ? 'rounded-tl-xl' : 'rounded-bl-xl'
                      } ${activeTab === tab.id
                        ? dark
                          ? 'bg-gray-950/80 backdrop-blur-2xl text-cyan-300 border-t border-l border-b border-cyan-400/30'
                          : 'bg-white/60 backdrop-blur-2xl text-cyan-700 border-t border-l border-b border-cyan-400/40'
                        : dark
                          ? 'bg-gray-950/50 backdrop-blur-sm text-gray-500 hover:text-gray-300 border-t border-l border-b border-gray-600/30 hover:border-gray-500/40 hover:bg-gray-950/70'
                          : 'bg-white/30 backdrop-blur-sm text-gray-500 hover:text-gray-700 border-t border-l border-b border-gray-400/30 hover:border-gray-400/50 hover:bg-white/50'
                      }`}
                    style={{ writingMode: 'vertical-lr', textOrientation: 'mixed' }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Form card — more transparent glass */}
              <div style={{ pointerEvents: 'auto' }} className={`relative z-20 w-full lg:w-[480px] xl:w-[520px] backdrop-blur-2xl rounded-2xl sm:rounded-l-none flex flex-col justify-center px-8 sm:px-12 py-12 sm:py-16 flex-1 ${dark
                ? 'bg-gray-950/50 border border-cyan-500/10'
                : 'bg-white/50 border border-gray-300/30'
                }`}>

                {/* Theme toggle — inside card, top-right */}
                <button
                  onClick={toggleTheme}
                  className={`absolute top-6 right-6 p-2 rounded-lg transition-all duration-300 ${dark
                    ? 'bg-gray-800/50 hover:bg-gray-700/60 text-yellow-400 border border-gray-700/30'
                    : 'bg-gray-200/50 hover:bg-gray-200/80 text-gray-600 border border-gray-300/30'
                    }`}
                >
                  {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>

                {/* Mobile-only branding + tabs */}
                <div className="sm:hidden mb-8">
                  <div className="mb-6">
                    <GradientText
                      colors={dark
                        ? ['#22d3ee', '#67e8f9', '#a5f3fc', '#22d3ee']
                        : ['#0e7490', '#0891b2', '#06b6d4', '#0e7490']
                      }
                      animationSpeed={6}
                      className="text-2xl font-bold"
                    >
                      BytesizedAI
                    </GradientText>
                  </div>
                  <div className="flex gap-2">
                    {TABS.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => { setActiveTab(tab.id); setAuthError(''); }}
                        className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeTab === tab.id
                          ? dark
                            ? 'bg-cyan-500/20 text-white border border-cyan-500/30'
                            : 'bg-cyan-500/15 text-cyan-800 border border-cyan-500/30'
                          : dark
                            ? 'text-gray-500 hover:text-gray-300'
                            : 'text-gray-400 hover:text-gray-600'
                          }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Heading */}
                <h2 className={`text-3xl font-bold mb-2 ${dark ? 'text-white' : 'text-gray-900'}`}>
                  {activeTab === 'login' ? 'Welcome back' : 'Create your account'}
                </h2>
                <p className={`text-sm ${authError ? 'mb-4' : 'mb-10'} ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {activeTab === 'login'
                    ? 'Sign in to continue to ByteSized AI Architect'
                    : 'Get started with ByteSized AI Architect'}
                </p>

                {/* Auth error banner */}
                {authError && (
                  <div className={`mb-6 px-4 py-3 rounded-xl text-sm border ${dark
                    ? 'bg-red-500/10 border-red-500/20 text-red-300'
                    : 'bg-red-50 border-red-200 text-red-700'
                  }`}>
                    {authError}
                  </div>
                )}

                {/* Verification UI Overlay */}
                {isVerifying ? (
                  <form onSubmit={handleConfirmCode} className="space-y-8 py-4">
                    <div className="text-center">
                      <p className={`text-sm ${dark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                        We've sent a 6-digit code to <span className="font-semibold text-cyan-500">{email}</span>. Please enter it below to confirm your account.
                      </p>
                      <div className="relative group">
                        <input
                          type="text"
                          value={confirmationCode}
                          onChange={(e) => setConfirmationCode(e.target.value)}
                          placeholder="000000"
                          maxLength={6}
                          disabled={isAuthLoading}
                          className={`w-full text-center text-4xl tracking-[0.5em] py-6 rounded-2xl border-2 transition-all duration-300 outline-none font-mono ${dark
                            ? 'bg-cyan-500/5 border-cyan-500/20 text-white placeholder-gray-700 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-500/10'
                            : 'bg-cyan-50/50 border-cyan-200 text-gray-900 placeholder-gray-300 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10'
                            } ${isAuthLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          autoFocus
                        />
                        <div className={`absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-300 ${dark ? 'bg-cyan-400/5 opacity-0 group-hover:opacity-100' : 'bg-cyan-500/5 opacity-0 group-hover:opacity-100'}`} />
                      </div>
                    </div>

                    <div className="flex flex-col gap-4 mt-8">
                      <button
                        type="submit"
                        disabled={isAuthLoading}
                        className="w-full relative overflow-hidden group py-4 rounded-xl text-base font-bold text-white transition-all duration-500 hover:scale-[1.02] active:scale-[0.98] shadow-lg disabled:cursor-not-allowed disabled:hover:scale-100"
                        style={{
                          background: 'linear-gradient(135deg, #0e7490, #0891b2, #06b6d4, #22d3ee)',
                          boxShadow: dark ? '0 10px 30px -10px rgba(6, 182, 212, 0.5)' : '0 10px 30px -10px rgba(6, 182, 212, 0.4)',
                        }}
                      >
                        <div className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${isAuthLoading ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}`}>
                          <div className="flex items-center gap-3">
                            <div className="relative w-5 h-5">
                              <div className="absolute inset-0 rounded-full border-2 border-white/20"></div>
                              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white animate-spin"></div>
                            </div>
                            <span className="text-sm tracking-wide font-medium">{loadingText}</span>
                          </div>
                        </div>
                        <span className={`flex justify-center items-center transition-all duration-500 ${isAuthLoading ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'}`}>
                          Verify Account
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setIsVerifying(false)}
                        disabled={isAuthLoading}
                        className={`w-full py-3 text-sm font-medium transition-colors ${dark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'
                          } ${isAuthLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Back to sign up
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {activeTab === 'signup' && (
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>Full name</label>
                        <div className="relative">
                          <User className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${dark ? 'text-gray-500' : 'text-gray-400'}`} />
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Jane Doe"
                            disabled={isAuthLoading}
                            className={`w-full pl-11 pr-4 py-3.5 rounded-xl border text-sm transition-all duration-200 outline-none ${dark
                              ? 'bg-white/5 border-gray-700/50 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20'
                              : 'bg-white/60 border-gray-300/50 text-gray-900 placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20'
                              } ${isAuthLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
                      <div className="relative">
                        <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${dark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          disabled={isAuthLoading}
                          className={`w-full pl-11 pr-4 py-3.5 rounded-xl border text-sm transition-all duration-200 outline-none ${dark
                            ? 'bg-white/5 border-gray-700/50 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20'
                            : 'bg-white/60 border-gray-300/50 text-gray-900 placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20'
                            } ${isAuthLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className={`text-sm font-medium ${dark ? 'text-gray-300' : 'text-gray-700'}`}>Password</label>
                        {activeTab === 'login' && !isAuthLoading && (
                          <button
                            type="button"
                            className={`text-xs font-medium transition-colors ${dark ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-500'
                              }`}
                          >
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <div className="relative">
                        <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${dark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          disabled={isAuthLoading}
                          className={`w-full pl-11 pr-4 py-3.5 rounded-xl border text-sm transition-all duration-200 outline-none ${dark
                            ? 'bg-white/5 border-gray-700/50 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20'
                            : 'bg-white/60 border-gray-300/50 text-gray-900 placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20'
                            } ${isAuthLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isAuthLoading}
                      className="w-full relative overflow-hidden group py-4 rounded-xl text-base font-bold text-white transition-all duration-500 hover:scale-[1.02] active:scale-[0.98] shadow-lg disabled:cursor-not-allowed disabled:hover:scale-100"
                      style={{
                        background: 'linear-gradient(135deg, #0e7490, #0891b2, #06b6d4, #22d3ee)',
                        boxShadow: dark ? '0 10px 30px -10px rgba(6, 182, 212, 0.5)' : '0 10px 30px -10px rgba(6, 182, 212, 0.4)',
                      }}
                    >
                      <div className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${isAuthLoading ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}`}>
                        <div className="flex items-center gap-3">
                          <div className="relative w-5 h-5">
                            <div className="absolute inset-0 rounded-full border-2 border-white/20"></div>
                            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white animate-spin"></div>
                          </div>
                          <span className="text-sm tracking-wide font-medium">{loadingText}</span>
                        </div>
                      </div>
                      <span className={`flex justify-center items-center transition-all duration-500 ${isAuthLoading ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'}`}>
                        {activeTab === 'login' ? 'Sign In' : 'Create Account'}
                      </span>
                      <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:transition-all group-hover:duration-1000 group-hover:translate-x-full" />
                    </button>
                  </form>
                )}

                {/* Divider */}
                <div className="relative flex items-center my-8">
                  <div className={`flex-grow h-px ${dark ? 'bg-gray-700/40' : 'bg-gray-300/50'}`} />
                  <span className={`px-4 text-xs font-medium ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {activeTab === 'signup' ? 'or sign up with' : 'or continue with'}
                  </span>
                  <div className={`flex-grow h-px ${dark ? 'bg-gray-700/40' : 'bg-gray-300/50'}`} />
                </div>
                {activeTab === 'signup' && (
                  <p className={`text-xs text-center -mt-4 mb-4 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Already have an account? You'll be signed in automatically.
                  </p>
                )}

                {/* Social buttons */}
                <div className="flex gap-4">
                  <button
                    onClick={() => handleSocialLogin('google')}
                    disabled={isAuthLoading}
                    className={`flex-1 flex items-center justify-center gap-2.5 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 border ${dark
                      ? 'bg-white/5 hover:bg-white/10 text-gray-200 border-gray-700/40'
                      : 'bg-white/60 hover:bg-white/80 text-gray-700 border-gray-300/50'
                      } ${isAuthLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    Google
                  </button>

                  <button
                    onClick={() => handleSocialLogin('github')}
                    disabled={isAuthLoading}
                    className={`flex-1 flex items-center justify-center gap-2.5 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 border ${dark
                      ? 'bg-white/5 hover:bg-white/10 text-gray-200 border-gray-700/40'
                      : 'bg-white/60 hover:bg-white/80 text-gray-700 border-gray-300/50'
                      } ${isAuthLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true" fill="currentColor">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                    </svg>
                    GitHub
                  </button>
                </div>
              </div>

              {/* ── Vertical text drawer (right side of form) ── */}
              <div
                className="hidden lg:flex items-center -ml-px z-10"
                style={{ pointerEvents: 'auto' }}
                onMouseEnter={() => setBannerHovered(true)}
                onMouseLeave={() => setBannerHovered(false)}
              >
                <div className="relative flex overflow-hidden transition-all duration-500 ease-in-out">
                  <div
                    className="flex items-center justify-center overflow-hidden transition-all duration-500 ease-in-out"
                    style={{
                      maxWidth: bannerHovered ? '50px' : '0px',
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                    }}
                  >
                    <span className={`whitespace-nowrap px-3 text-lg font-medium ${dark ? 'text-cyan-200' : 'text-cyan-800'}`}>
                      Working AI proof of concept. Before you invest.
                    </span>
                  </div>

                  <div
                    className="flex items-center gap-3 px-4 py-8"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                  >
                    <span className={`text-2xl whitespace-nowrap ${dark ? 'text-gray-300' : 'text-gray-600'}`}>Will a</span>
                    <RotatingText
                      texts={ROTATING_EXAMPLES}
                      rotationInterval={2500}
                      splitBy="characters"
                      staggerDuration={0.025}
                      staggerFrom="first"
                      mainClassName={`text-3xl font-bold whitespace-nowrap ${dark ? 'text-cyan-300' : 'text-cyan-600'}`}
                      transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                      initial={{ y: '100%', opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: '-100%', opacity: 0 }}
                    />
                    <span className={`text-2xl whitespace-nowrap ${dark ? 'text-gray-300' : 'text-gray-600'}`}>actually work?</span>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
